import { ArrowUpRight, Ban, ChevronRight, ClipboardList, Link2, Send } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { Alert, ButtonGroup } from 'common-src/components/base';
import { getDashboardClients } from 'common-src/models/Client';

import { CLIENT_ROOT } from 'src/constants/paths';
import useCustomSelector from 'src/hooks/useCustomSelector';
import useTabs from 'src/hooks/useTabs';

import {
  BLOCKED_REASON_LABELS,
  BLOCKED_REASON_OPTIONS,
  BLOCKED_STATUS,
  ESCALATED_TO_OPTIONS,
  PRIORITY_BADGE_COPY,
  teamMeta,
} from '../../constants';
import {
  formatDue,
  getIncompleteDependencyTasks,
  getLinkedTaskWaitingForTitle,
  getTaskDecisionOptions,
  getTaskDisplayStatusLabel,
  getTaskDisplayTitle,
  isAutomaticWorkflowTask,
  isLinkedTask,
  isTaskCanceled,
  isTaskEscalatedStatus,
  isTaskReadinessComplete,
  isTaskUnableToComplete,
  isWorkflowBlockedTask,
} from '../../helpers';
import { Badge } from '../Badge';
import EscalateTaskModal from '../EscalateTaskModal';
import LeadResourceCallForm from '../LeadResourceCallForm';
import ReassignModal from '../ReassignModal';
import styles from '../TaskDetail/TaskDetail.module.scss';
import leadStyles from './LeadTaskDetail.module.scss';

const WORK_LEAD_TASK_KEY = 'work_lead';
const CONSENT_TASK_KEY = 'send_consent_form';
const CREATE_PATIENT_TASK_KEY = 'create_patient_from_lead_manual';
const WORK_LEAD_DECISION = { done: 'done', disqualified: 'disqualified' };

const LEAD_STATUS_LABELS = {
  NEW: 'New',
  CONTACTED: 'Contacted',
  QUALIFIED: 'Qualified',
  DISQUALIFIED: 'Disqualified',
  ENROLLED: 'Enrolled',
};

const formatTimestamp = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const LeadTaskDetail = ({
  task,
  relatedTasks = [],
  currentUserId,
  onClose,
  onAssignToMe,
  isAssigning,
  onReassign,
  isReassigning,
  onUnassign,
  isUnassigning,
  onEscalate,
  onDeescalate,
  isEscalating,
  onBlock,
  onUnblock,
  isBlocking,
  onMarkComplete,
  isMarking,
  onSendConsent,
  isSendingConsent,
  onCreatePatient,
  isCreatingPatient,
  onDisqualify,
  onLeadUpdated,
  onOpenWorkModal,
}) => {
  const isCreatePatientTask = task?.taskKey === CREATE_PATIENT_TASK_KEY;
  const isWorkLeadTask = task?.taskKey === WORK_LEAD_TASK_KEY;
  const formOpenByDefault = isCreatePatientTask || isWorkLeadTask;
  const [isLeadFormOpen, setIsLeadFormOpen] = useState(formOpenByDefault);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [showBlockDropdown, setShowBlockDropdown] = useState(false);
  const blockDropdownRef = useRef(null);
  const clients = useCustomSelector((state) => getDashboardClients(state)) || [];
  const { onSelectTab } = useTabs();
  useEffect(() => {
    setIsLeadFormOpen(formOpenByDefault);
  }, [task?.id, formOpenByDefault]);
  useEffect(() => {
    setShowEscalateModal(false);
    setShowReassignModal(false);
    setShowBlockDropdown(false);
  }, [task?.id]);
  useEffect(() => {
    if (!showBlockDropdown) return undefined;

    const handleClickOutside = (event) => {
      if (blockDropdownRef.current?.contains(event.target)) return;
      setShowBlockDropdown(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showBlockDropdown]);

  if (!task) return null;

  const lead = task.patient || {};
  const patientCreated = lead.patientId != null;
  const team = teamMeta[task.team];
  const due = task.due ? formatDue(task.due) : { text: '—', urgent: false };
  const dueDateText = task.due ? String(task.due).split('T')[0] : '—';
  const taskTitle = getTaskDisplayTitle(task);
  const taskDescription = `${task.taskDescription || ''}`.trim();
  const taskSop = `${task.taskSop || ''}`.trim();
  const hasTaskSop = !!taskSop;
  const taskPriority = task.priority;
  const displayStatusLabel = getTaskDisplayStatusLabel(task);

  const isAutomaticTask = isAutomaticWorkflowTask(task);
  const isCompletedTask = isTaskReadinessComplete(task);
  const isCanceledTask = isTaskCanceled(task);
  const isUnableToCompleteTask = isTaskUnableToComplete(task);
  const isUnassigned = task.assignee == null;
  const isClaimedByYou = !isUnassigned && `${task.assignee}` === `${currentUserId ?? ''}`;
  const hasOpenPrerequisite = getIncompleteDependencyTasks(task, relatedTasks).length > 0;
  const isPrerequisiteBlockedTask =
    !isCompletedTask &&
    !isAutomaticTask &&
    !task.workflowReady &&
    (hasOpenPrerequisite || isLinkedTask(task));
  const isBlockedByDependencies = isWorkflowBlockedTask(task);
  const prerequisiteTaskTitle = isPrerequisiteBlockedTask
    ? getLinkedTaskWaitingForTitle(task, relatedTasks)
    : null;
  const isWaitingOnWorkflow =
    !isAutomaticTask && !isCompletedTask && !isPrerequisiteBlockedTask && !task.workflowReady;
  const assignedTo = isUnassigned ? 'Unassigned' : task.owner || 'Assigned';

  const clientNameById = (clientId) => {
    if (clientId == null) return null;
    const client = clients.find((item) => `${item?.id ?? ''}` === `${clientId}`);
    if (!client) return null;
    return [client.firstName, client.lastName].filter(Boolean).join(' ') || null;
  };
  const isEscalatedStatus = isTaskEscalatedStatus(task);
  const escalatedToOption = ESCALATED_TO_OPTIONS.find(
    (option) => option.value === Number(task.escalatedTo),
  );
  const escalatedToLabel =
    clientNameById(task.escalatedToUser) ||
    (escalatedToOption ? `${escalatedToOption.label} team` : null);
  const escalatedByLabel = clientNameById(task.escalatedBy);
  const escalatedAtLabel = formatTimestamp(task.escalatedAt);
  const canEscalateTask = !isAutomaticTask && !isCompletedTask && !isCanceledTask;

  const isBlockedStatus = task.blockedStatus === BLOCKED_STATUS.Blocked;
  const blockedReasonLabel =
    task.blockedReason != null ? (BLOCKED_REASON_LABELS[task.blockedReason] ?? 'Blocked') : null;
  const blockedByLabel = clientNameById(task.blockedBy);
  const blockedAtLabel = formatTimestamp(task.blockedAt);

  const decisionOptions = getTaskDecisionOptions(task);
  const hasDecisionActions = decisionOptions.length > 0;
  const showCompletionActions =
    !isAutomaticTask &&
    !isCompletedTask &&
    !isPrerequisiteBlockedTask &&
    (isWorkLeadTask || !patientCreated) &&
    isClaimedByYou &&
    task.workflowReady;

  const isConsentTask = task.taskKey === CONSENT_TASK_KEY;
  const consentDispatch = task.metadata?.consent_dispatch || null;
  const consentSentAtValue = consentDispatch?.sent_at || lead.consentSentAt || null;
  const consentSentAt = formatTimestamp(consentSentAtValue);
  const showConsentCard = isConsentTask || isWorkLeadTask;
  const consentAlreadySent = !!consentSentAtValue;
  const consentAccepted = lead.status === 'ENROLLED';
  const manualCreateTask = relatedTasks.find(
    (candidate) =>
      candidate.taskKey === CREATE_PATIENT_TASK_KEY &&
      ![3, 4, 5].includes(Number(candidate.status)) &&
      candidate.workflowReady,
  );
  const needsManualPatient =
    !patientCreated && (!!manualCreateTask || (isCreatePatientTask && !!task.workflowReady));
  const outcomeSteps = [
    { key: 'sent', label: 'Consent sent', done: consentAlreadySent, detail: consentSentAt },
    { key: 'accepted', label: 'Consent accepted', done: consentAccepted },
    {
      key: 'patient',
      label: patientCreated
        ? `Patient #${lead.patientId} created`
        : needsManualPatient
          ? 'Patient needs manual creation'
          : 'Patient created',
      done: patientCreated,
    },
  ];

  const leadStatusLabel = LEAD_STATUS_LABELS[lead.status] || lead.status || '—';
  const leadContact = [lead.phone, lead.email].filter(Boolean).join(' · ') || '—';

  const completedBanner = isCompletedTask && (
    <div
      className={
        isCanceledTask
          ? styles.canceledBanner
          : isUnableToCompleteTask
            ? styles.unableToCompleteBanner
            : styles.completedBanner
      }
    >
      <div
        className={
          isCanceledTask
            ? styles.canceledLabel
            : isUnableToCompleteTask
              ? styles.unableToCompleteLabel
              : styles.completedLabel
        }
      >
        <span
          className={
            isCanceledTask
              ? styles.canceledMark
              : isUnableToCompleteTask
                ? styles.unableToCompleteMark
                : styles.completedCheck
          }
          aria-hidden="true"
        >
          ✓
        </span>
        {isCanceledTask ? 'Canceled' : isUnableToCompleteTask ? 'Unable to Complete' : 'Completed'}
      </div>
    </div>
  );

  const systemBanner = !isCompletedTask && isAutomaticTask && (
    <div className={styles.systemBanner}>
      <div className={styles.systemBannerCopy}>
        <div className={styles.systemLabel}>Handled by system</div>
        <div className={styles.systemText}>
          This automatic workflow step runs on its own and the workflow advances when it finishes —
          there is nothing to claim or complete here.
        </div>
      </div>
      {task.callbackStatus ? <Badge variant="outline">{task.callbackStatus}</Badge> : null}
    </div>
  );

  const claimedBanner = !isAutomaticTask && !isCompletedTask && isClaimedByYou && (
    <div className={styles.claimedBanner}>
      <div className={styles.claimedBannerCopy}>
        <div className={styles.claimedLabel}>
          <span className={styles.claimedDot} aria-hidden="true" />
          Claimed by you
        </div>
      </div>
    </div>
  );

  const blockedByPrerequisiteBanner = isPrerequisiteBlockedTask && (
    <div className={styles.blockedBanner}>
      <div className={styles.blockedBannerIconWrap} aria-hidden="true">
        <Link2 className={styles.blockedBannerIcon} />
      </div>
      <div className={styles.blockedBannerCopy}>
        <div className={styles.blockedLabel}>
          {isBlockedByDependencies ? 'Blocked by prerequisites' : 'Blocked by prerequisite'}
        </div>
        {prerequisiteTaskTitle ? (
          <div className={styles.blockedWaitingText}>
            <span className={styles.blockedWaitingLabel}>
              {isBlockedByDependencies ? 'Blocked by:' : 'Waiting on:'}
            </span>{' '}
            {prerequisiteTaskTitle}
          </div>
        ) : (
          <div className={styles.blockedWaitingText}>
            Unlocks when the preceding workflow step completes.
          </div>
        )}
      </div>
    </div>
  );

  const waitingBanner = isWaitingOnWorkflow && (
    <div className={styles.blockedBanner}>
      <div className={styles.blockedBannerIconWrap} aria-hidden="true">
        <Link2 className={styles.blockedBannerIcon} />
      </div>
      <div className={styles.blockedBannerCopy}>
        <div className={styles.blockedLabel}>Waiting on the workflow</div>
        <div className={styles.blockedWaitingText}>
          The preceding step has not finished yet. This task can be claimed now and completed once
          the workflow reaches it.
        </div>
      </div>
    </div>
  );

  const claimBanner = !isAutomaticTask && !isCompletedTask && isUnassigned && (
    <div className={styles.unclaimedBanner}>
      <div className={styles.unclaimedBannerCopy}>
        <div className={styles.unclaimedLabel}>Unclaimed</div>
        <div className={styles.unclaimedText}>
          Claim to start working &middot; others will see it&apos;s taken
        </div>
      </div>
      <div className={styles.unclaimedBannerActions}>
        <button
          type="button"
          className={styles.claimButton}
          disabled={isAssigning}
          onClick={() => onAssignToMe?.(task.id)}
        >
          {isAssigning ? 'Claiming...' : 'Claim'}
        </button>
        {onReassign ? (
          <button
            type="button"
            className={styles.secondaryAction}
            disabled={isReassigning}
            onClick={() => setShowReassignModal(true)}
          >
            Assign to...
          </button>
        ) : null}
      </div>
    </div>
  );

  const assignedBanner = !isAutomaticTask && !isCompletedTask && !isUnassigned && (
    <div className={styles.unclaimedBanner}>
      <div className={styles.unclaimedBannerCopy}>
        <div className={styles.unclaimedLabel}>
          {isClaimedByYou ? 'Claimed by you' : `Owned by ${assignedTo}`}
        </div>
        <div className={styles.unclaimedText}>
          {isClaimedByYou
            ? 'Unclaim to put it back in the queue, or hand it to someone else'
            : 'Only the owner can complete this task'}
        </div>
      </div>
      <div className={styles.unclaimedBannerActions}>
        {isClaimedByYou && onUnassign ? (
          <button
            type="button"
            className={styles.secondaryAction}
            disabled={isUnassigning}
            onClick={() => onUnassign(task.id)}
          >
            {isUnassigning ? 'Unclaiming...' : 'Unclaim'}
          </button>
        ) : null}
        {onReassign ? (
          <button
            type="button"
            className={styles.secondaryAction}
            disabled={isReassigning}
            onClick={() => setShowReassignModal(true)}
          >
            {isClaimedByYou ? 'Assign to...' : 'Reassign'}
          </button>
        ) : null}
      </div>
    </div>
  );

  const handleBlockSelect = () => {
    if (isBlocking) return;
    if (isBlockedStatus) {
      onUnblock?.(task.id);
      return;
    }
    setShowBlockDropdown((prev) => !prev);
  };

  const handleEscalateSelect = () => {
    if (isEscalating) return;
    if (isEscalatedStatus) {
      onDeescalate?.(task.id);
      return;
    }
    setShowBlockDropdown(false);
    setShowEscalateModal(true);
  };

  const statusOptions = [
    (onBlock || onUnblock) && {
      value: 'block',
      label: (
        <span className={styles.statusActionLabel}>
          <Ban size={14} aria-hidden="true" />
          {isBlocking ? 'Working...' : isBlockedStatus ? 'Unblock' : 'Blocked'}
        </span>
      ),
      active: isBlockedStatus,
      activeTone: 'activeDanger',
      disabled: isBlocking,
    },
    (onEscalate || onDeescalate) && {
      value: 'escalate',
      label: (
        <span className={styles.statusActionLabel}>
          <ArrowUpRight size={14} aria-hidden="true" />
          {isEscalating ? 'Working...' : isEscalatedStatus ? 'De-escalate' : 'Escalate'}
        </span>
      ),
      active: isEscalatedStatus,
      activeTone: 'activeWarning',
      disabled: isEscalating,
    },
  ].filter(Boolean);

  const statusActionsRow = canEscalateTask && statusOptions.length > 0 && (
    <div className={styles.sopSection}>
      <div className={styles.secondaryActionsGroup}>
        <div className={styles.statusActionWrap} ref={blockDropdownRef}>
          <ButtonGroup
            label="Task status"
            options={statusOptions}
            onSelect={(value) => (value === 'block' ? handleBlockSelect() : handleEscalateSelect())}
          />
          {showBlockDropdown ? (
            <div className={styles.blockDropdown}>
              {BLOCKED_REASON_OPTIONS.map((reason) => (
                <button
                  key={reason.value}
                  type="button"
                  className={styles.blockDropdownItem}
                  onClick={async () => {
                    const blocked = (await onBlock?.(task.id, reason.value)) ?? false;
                    if (blocked) setShowBlockDropdown(false);
                  }}
                >
                  {reason.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  const blockedMeta = [blockedByLabel ? `Set by ${blockedByLabel}` : null, blockedAtLabel]
    .filter(Boolean)
    .join(' · ');
  const blockedStatusBanner = !isCompletedTask && isBlockedStatus && (
    <Alert
      variant="destructive"
      role="status"
      flush
      inlineActions
      icon={Ban}
      title={`Blocked${blockedReasonLabel ? ` · ${blockedReasonLabel}` : ''}`}
      actions={
        onUnblock ? (
          <button
            type="button"
            className={styles.alertAction}
            disabled={isBlocking}
            onClick={() => onUnblock(task.id)}
          >
            {isBlocking ? 'Unblocking...' : 'Unblock'}
          </button>
        ) : null
      }
    >
      {blockedMeta || null}
    </Alert>
  );

  const escalatedMeta = [
    task.escalatedReason,
    escalatedByLabel ? `Set by ${escalatedByLabel}` : null,
    escalatedAtLabel,
  ]
    .filter(Boolean)
    .join(' · ');
  const escalatedStatusBanner = !isCompletedTask && isEscalatedStatus && (
    <Alert
      variant="warning"
      role="status"
      flush
      inlineActions
      icon={ArrowUpRight}
      title={`Escalated${escalatedToLabel ? ` → ${escalatedToLabel}` : ''}`}
      actions={
        onDeescalate ? (
          <button
            type="button"
            className={styles.alertAction}
            disabled={isEscalating}
            onClick={() => onDeescalate(task.id)}
          >
            {isEscalating ? 'De-escalating...' : 'De-escalate'}
          </button>
        ) : null
      }
    >
      {escalatedMeta || null}
    </Alert>
  );

  const workLeadCompletionActions = showCompletionActions && isWorkLeadTask && (
    <div className={styles.sectionActions}>
      <div className={styles.primaryActionSection}>
        <div className={styles.primaryActionsRow}>
          <button
            type="button"
            className={styles.primaryAction}
            disabled={isMarking || !consentAlreadySent}
            title={
              consentAlreadySent
                ? ''
                : 'Send the consent form (or disqualify the lead) before completing this step'
            }
            onClick={() =>
              onMarkComplete?.(task, {
                result: WORK_LEAD_DECISION.done,
                decision: WORK_LEAD_DECISION.done,
              })
            }
          >
            {isMarking ? 'Marking...' : 'Mark Complete'}
          </button>
          <button
            type="button"
            className={`${styles.secondaryAction} ${styles.cancelTaskAction}`}
            disabled={isMarking || consentAlreadySent}
            title={
              consentAlreadySent
                ? 'The consent form has been sent; the lead can no longer be disqualified here'
                : ''
            }
            onClick={() =>
              onDisqualify
                ? onDisqualify(task)
                : onMarkComplete?.(task, {
                    result: WORK_LEAD_DECISION.disqualified,
                    decision: WORK_LEAD_DECISION.disqualified,
                  })
            }
          >
            {isMarking ? 'Marking...' : 'Disqualify lead'}
          </button>
        </div>
      </div>
    </div>
  );

  const outcomeStrip = (isWorkLeadTask || isCreatePatientTask) && (
    <div className={leadStyles.outcomeStrip}>
      {outcomeSteps.map((step) => (
        <div
          key={step.key}
          className={`${leadStyles.outcomeStep} ${step.done ? leadStyles.outcomeStepDone : ''}`}
        >
          <span className={leadStyles.outcomeMark} aria-hidden="true">
            {step.done ? '✓' : '○'}
          </span>
          <span className={leadStyles.outcomeLabel}>{step.label}</span>
          {step.done && step.detail ? (
            <span className={leadStyles.outcomeDetail}>{step.detail}</span>
          ) : null}
        </div>
      ))}
      {needsManualPatient && !isCreatePatientTask ? (
        <span className={leadStyles.outcomeHint}>
          Finish it on the &ldquo;Create patient from lead manually&rdquo; step
        </span>
      ) : null}
      {patientCreated ? (
        <span className={leadStyles.outcomeActions}>
          <button
            type="button"
            className={leadStyles.outcomePatientLink}
            onClick={() =>
              onSelectTab({ path: `${CLIENT_ROOT}/patients/details/${lead.patientId}/info` })
            }
          >
            See patient chart ↗
          </button>
          <button
            type="button"
            className={leadStyles.outcomePatientLink}
            onClick={() =>
              onSelectTab({
                path: `${CLIENT_ROOT}/patients/details/${lead.patientId}/known-plans`,
              })
            }
          >
            Add insurance ↗
          </button>
        </span>
      ) : null}
    </div>
  );

  const consentSection = showConsentCard && (
    <div className={styles.sectionEvidence}>
      <div className={styles.sectionLabel}>Consent form</div>
      <div className={styles.evidenceCard}>
        <div className={styles.evidenceHeader}>
          <span className={styles.evidenceIcon} aria-hidden="true">
            <Send size={12} strokeWidth={2} />
          </span>
          <span className={styles.evidenceLabel}>
            {consentDispatch ? `Sent ${consentSentAt || ''}`.trim() : 'Not sent yet'}
          </span>
        </div>
        <p className={styles.evidenceSummary}>
          {consentDispatch ? (
            // Kept readable once the patient exists, but no longer a live link: the form
            // has nothing left to collect.
            patientCreated ? (
              <span className={leadStyles.consentLinkSpent} title="The patient already exists">
                {consentDispatch.consent_url}
              </span>
            ) : (
              <a
                className={leadStyles.consentLink}
                href={consentDispatch.consent_url}
                target="_blank"
                rel="noreferrer"
              >
                {consentDispatch.consent_url}
              </a>
            )
          ) : (
            'Sending texts the lead their prefilled consent form and posts the link to Slack.'
          )}
        </p>
        {!isCompletedTask ? (
          <div className={leadStyles.consentActions}>
            <button
              type="button"
              className={styles.primaryAction}
              disabled={isSendingConsent || !isClaimedByYou || patientCreated}
              title={
                (patientCreated && 'The patient already exists; consent is no longer needed') ||
                (!isClaimedByYou && 'Claim this task before sending the consent form') ||
                ''
              }
              onClick={() => onSendConsent?.(task)}
            >
              {isSendingConsent
                ? 'Sending...'
                : consentDispatch
                  ? 'Resend consent form'
                  : 'Send consent form'}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className={styles.root}>
      {completedBanner}
      {systemBanner}
      {blockedStatusBanner}
      {escalatedStatusBanner}
      {claimedBanner}
      {outcomeStrip}
      {blockedByPrerequisiteBanner}
      {waitingBanner}

      <div className={styles.sectionHeader}>
        <div className={styles.headerTop}>
          <div className={styles.title}>{taskTitle}</div>
          <button type="button" onClick={onClose} className={styles.closeButton}>
            ×
          </button>
        </div>
        {taskDescription ? <div className={styles.taskDescription}>{taskDescription}</div> : null}
        <div className={styles.badgesRow}>
          {!isCompletedTask ? (
            <Badge variant={isUnableToCompleteTask ? 'orange' : 'default'}>
              {displayStatusLabel}
            </Badge>
          ) : null}
          {lead.businessUnit ? <Badge variant="outline">{lead.businessUnit}</Badge> : null}
          <span
            className={`${styles.priorityBadge} ${styles[`priorityBadge${taskPriority}`] || ''}`}
          >
            {PRIORITY_BADGE_COPY[taskPriority] ?? taskPriority}
          </span>
        </div>
      </div>

      <div className={styles.sectionInfo}>
        <div className={styles.infoGrid}>
          {[
            ['Lead', lead.name || '—'],
            ['Contact', leadContact],
            ['Lead status', leadStatusLabel],
            ['Assigned to', assignedTo],
            ['Team', team ? `${team.icon} ${team.label}` : '—'],
            ['Due', [dueDateText, due.text].filter(Boolean).join('  ')],
          ].map(([key, value]) => (
            <div key={key} className={styles.infoRow}>
              <span className={styles.infoLabel}>{key}</span>
              <span className={styles.infoValue}>{value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.sopSection}>
        <button
          type="button"
          className={`${styles.sopButton} ${!hasTaskSop ? styles.sopButtonDisabled : ''}`}
          disabled={!hasTaskSop}
          onClick={() => {
            if (!hasTaskSop) return;
            window.open(taskSop, '_blank', 'noopener,noreferrer');
          }}
        >
          <span className={styles.sopButtonIcon} aria-hidden="true">
            📋
          </span>
          <span className={styles.sopButtonLabel}>Read SOP for this task</span>
          <span className={styles.sopButtonArrow} aria-hidden="true">
            ↗
          </span>
        </button>
      </div>

      {(isWorkLeadTask || isCreatePatientTask) && onOpenWorkModal ? (
        <div className={styles.sopSection}>
          <button type="button" className={styles.sopButton} onClick={() => onOpenWorkModal(task)}>
            <span className={styles.sopButtonIcon} aria-hidden="true">
              <ClipboardList size={14} strokeWidth={2} />
            </span>
            <span className={styles.sopButtonLabel}>Work this lead in a focused view</span>
            <span className={styles.sopButtonArrow} aria-hidden="true">
              ↗
            </span>
          </button>
        </div>
      ) : null}

      {consentSection}

      {workLeadCompletionActions}
      {claimBanner}
      {assignedBanner}
      {statusActionsRow}

      <div className={styles.linkedTasksSection}>
        <button
          type="button"
          className={styles.linkedTasksHeader}
          onClick={() => setIsLeadFormOpen((prev) => !prev)}
          aria-expanded={isLeadFormOpen}
        >
          <ClipboardList className={styles.linkedTasksHeaderIcon} aria-hidden="true" />
          <span className={styles.linkedTasksHeaderLabel}>Lead details</span>
          <ChevronRight
            className={`${styles.linkedTasksHeaderChevron} ${
              isLeadFormOpen ? styles.linkedTasksHeaderChevronExpanded : ''
            }`}
            aria-hidden="true"
          />
        </button>
        {isLeadFormOpen ? (
          <LeadResourceCallForm
            leadResourceId={task.leadResourceId}
            onLeadUpdated={onLeadUpdated}
            onCreatePatient={
              isCreatePatientTask &&
              isClaimedByYou &&
              !patientCreated &&
              consentAccepted &&
              task.workflowReady
                ? onCreatePatient
                : null
            }
            isCreatingPatient={isCreatingPatient}
            title="Lead details"
          />
        ) : null}
      </div>

      {showCompletionActions && !isWorkLeadTask && (
        <div className={styles.sectionActions}>
          <div className={styles.primaryActionSection}>
            <div className={styles.primaryActionsRow}>
              {hasDecisionActions ? (
                decisionOptions.map((decisionOption) => (
                  <button
                    key={decisionOption.value}
                    type="button"
                    className={styles.primaryAction}
                    disabled={isMarking}
                    onClick={() =>
                      onMarkComplete?.(task, {
                        result: decisionOption.value,
                        decision: decisionOption.value,
                      })
                    }
                  >
                    {isMarking ? 'Marking...' : decisionOption.label}
                  </button>
                ))
              ) : (
                <button
                  type="button"
                  className={styles.primaryAction}
                  disabled={isMarking}
                  onClick={() => onMarkComplete?.(task, null)}
                >
                  {isMarking ? 'Marking...' : 'Mark Complete'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showReassignModal ? (
        <ReassignModal
          task={{ ...task, title: taskTitle, owner: assignedTo }}
          onClose={() => setShowReassignModal(false)}
          isSubmitting={isReassigning}
          onConfirm={async (client) => {
            if (!client?.id || !task?.id) return false;
            return (await onReassign?.(task.id, client.id)) ?? false;
          }}
        />
      ) : null}

      {showEscalateModal ? (
        <EscalateTaskModal
          task={task}
          onClose={() => setShowEscalateModal(false)}
          isSubmitting={isEscalating}
          currentUserName={clientNameById(currentUserId) || ''}
          onConfirm={async (escalationPayload) =>
            (await onEscalate?.(task.id, escalationPayload)) ?? false
          }
        />
      ) : null}
    </div>
  );
};

LeadTaskDetail.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.string,
    leadResourceId: PropTypes.string,
    taskKey: PropTypes.string,
    title: PropTypes.string,
    taskDescription: PropTypes.string,
    taskSop: PropTypes.string,
    priority: PropTypes.string,
    status: PropTypes.number,
    team: PropTypes.string,
    owner: PropTypes.string,
    assignee: PropTypes.number,
    due: PropTypes.string,
    workflowReady: PropTypes.bool,
    callbackStatus: PropTypes.string,
    metadata: PropTypes.object,
    patient: PropTypes.object,
    blockedStatus: PropTypes.number,
    blockedReason: PropTypes.number,
    blockedBy: PropTypes.number,
    blockedAt: PropTypes.string,
    escalatedStatus: PropTypes.number,
    escalatedTo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    escalatedToUser: PropTypes.number,
    escalatedBy: PropTypes.number,
    escalatedAt: PropTypes.string,
    escalatedReason: PropTypes.string,
  }),
  relatedTasks: PropTypes.arrayOf(PropTypes.object),
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func,
  onAssignToMe: PropTypes.func,
  onReassign: PropTypes.func,
  isReassigning: PropTypes.bool,
  isAssigning: PropTypes.bool,
  onUnassign: PropTypes.func,
  isUnassigning: PropTypes.bool,
  onEscalate: PropTypes.func,
  onDeescalate: PropTypes.func,
  isEscalating: PropTypes.bool,
  onBlock: PropTypes.func,
  onUnblock: PropTypes.func,
  isBlocking: PropTypes.bool,
  onMarkComplete: PropTypes.func,
  isMarking: PropTypes.bool,
  onSendConsent: PropTypes.func,
  isSendingConsent: PropTypes.bool,
  onCreatePatient: PropTypes.func,
  isCreatingPatient: PropTypes.bool,
  onDisqualify: PropTypes.func,
  onLeadUpdated: PropTypes.func,
  onOpenWorkModal: PropTypes.func,
};

export default LeadTaskDetail;
